import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, Button, Input, Alert, Tag, Table, Typography, Space, Popconfirm } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  SafetyOutlined,
  UnlockOutlined,
  CheckOutlined,
  CopyOutlined,
  DownloadOutlined,
  EyeInvisibleOutlined,
  ReloadOutlined,
  LogoutOutlined,
} from '@ant-design/icons';
import { api, User, QuotaLedgerRow } from '../api';
import { t } from '../i18n';
import { fmtDateTimeMin } from '../time';

/** Best-effort friendly device label from a User-Agent string. */
function deviceLabel(ua: string | null): string {
  if (!ua) return t('authSessionUnknownDevice');
  // iOS must be checked before macOS: iPhone/iPad UAs contain "like Mac OS X".
  const os = /Windows/i.test(ua)
    ? 'Windows'
    : /iPhone|iPad|iPod/i.test(ua)
      ? 'iOS'
      : /Android/i.test(ua)
        ? 'Android'
        : /Mac OS X|Macintosh/i.test(ua)
          ? 'macOS'
          : /Linux/i.test(ua)
            ? 'Linux'
            : '';
  const br = /Edg\//i.test(ua)
    ? 'Edge'
    : /OPR\/|Opera/i.test(ua)
      ? 'Opera'
      : /Chrome\//i.test(ua)
        ? 'Chrome'
        : /Firefox\//i.test(ua)
          ? 'Firefox'
          : /Safari\//i.test(ua)
            ? 'Safari'
            : '';
  return [br, os].filter(Boolean).join(' · ') || ua.slice(0, 40);
}

interface Props {
  user: User;
}

export function Account({ user }: Props) {
  const qc = useQueryClient();
  const status = useQuery({ queryKey: ['totp-status'], queryFn: api.totpStatus });

  const [enrollment, setEnrollment] = useState<{ secret: string; otpauthUri: string } | null>(null);
  const [code, setCode] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);

  const begin = useMutation({
    mutationFn: api.totpBegin,
    onSuccess: (r) => {
      setEnrollment(r);
      setErr(null);
      setOk(null);
    },
  });
  const verify = useMutation({
    mutationFn: () => api.totpVerify(code),
    onSuccess: (r) => {
      setEnrollment(null);
      setCode('');
      setErr(null);
      setOk(t('totpEnabledOk'));
      setRecoveryCodes(r.recoveryCodes ?? null);
      qc.invalidateQueries({ queryKey: ['totp-status'] });
      qc.invalidateQueries({ queryKey: ['recovery-codes-count'] });
    },
    onError: () => setErr(t('totpFailed')),
  });
  const disable = useMutation({
    mutationFn: api.totpDisable,
    onSuccess: () => {
      setOk(null);
      setErr(null);
      qc.invalidateQueries({ queryKey: ['totp-status'] });
    },
  });

  const copySecret = async (): Promise<void> => {
    if (!enrollment) return;
    try {
      await navigator.clipboard.writeText(enrollment.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked */
    }
  };

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card title={t('accountTitle')}>
        <Typography.Text type="secondary">
          {user.username}
          {user.email ? <> · {user.email}</> : null} · <Tag>{user.role}</Tag> · id{' '}
          <Typography.Text code>{user.id}</Typography.Text>
        </Typography.Text>
      </Card>

      <Card title={t('totpSection')} loading={status.isLoading}>
        {status.data && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Typography.Paragraph>
              {status.data.enabled ? t('totpEnabledNote') : t('totpDisabledNote')}
            </Typography.Paragraph>
            {!status.data.enabled && !enrollment && (
              <Button type="primary" icon={<SafetyOutlined />} loading={begin.isPending} onClick={() => begin.mutate()}>
                {t('totpBeginBtn')}
              </Button>
            )}
            {status.data.enabled && (
              <Popconfirm title={t('totpDisableBtn') + '?'} onConfirm={() => disable.mutate()}>
                <Button danger icon={<UnlockOutlined />} loading={disable.isPending}>
                  {t('totpDisableBtn')}
                </Button>
              </Popconfirm>
            )}

            {enrollment && (
              <Card type="inner" style={{ marginTop: 8 }}>
                <Typography.Text type="secondary">{t('totpSecretLabel')}</Typography.Text>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
                  <Typography.Text code style={{ fontSize: 14, letterSpacing: 1 }}>
                    {enrollment.secret}
                  </Typography.Text>
                  <Button size="small" icon={<CopyOutlined />} onClick={copySecret}>
                    {copied ? 'Copied' : t('totpSecretCopy')}
                  </Button>
                </div>
                <Typography.Paragraph type="secondary" style={{ fontSize: 11, wordBreak: 'break-all', marginTop: 6 }}>
                  {enrollment.otpauthUri}
                </Typography.Paragraph>
                <div style={{ marginTop: 12 }}>
                  <div style={{ marginBottom: 4 }}>
                    <Typography.Text type="secondary">{t('totpCodeInput')}</Typography.Text>
                  </div>
                  <Space>
                    <Input
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      maxLength={6}
                      style={{ width: 130, fontSize: 18, letterSpacing: 4, textAlign: 'center' }}
                      inputMode="numeric"
                      autoFocus
                    />
                    <Button type="primary" icon={<CheckOutlined />} disabled={code.length !== 6} loading={verify.isPending} onClick={() => verify.mutate()}>
                      {t('totpVerifyBtn')}
                    </Button>
                  </Space>
                </div>
              </Card>
            )}

            {err && <Alert type="error" showIcon message={err} />}
            {ok && <Alert type="success" showIcon message={ok} />}
          </Space>
        )}
      </Card>

      <ClientQuotaHistory />
      <RecoveryCodesPanel justIssued={recoveryCodes} dismiss={() => setRecoveryCodes(null)} />
      <ActiveSessions />
    </Space>
  );
}

function ClientQuotaHistory() {
  const q = useQuery({ queryKey: ['my-quota-ledger'], queryFn: () => api.myQuotaLedger({ limit: 100 }) });
  const fmt = fmtDateTimeMin;
  const typeTag: Record<QuotaLedgerRow['type'], { color: string; label: string }> = {
    topup: { color: 'green', label: t('quotaTypeTopup') },
    spend: { color: 'red', label: t('quotaTypeSpend') },
    adjust: { color: 'default', label: t('quotaTypeAdjust') },
    expire: { color: 'orange', label: t('quotaTypeExpire') },
  };
  const columns: ColumnsType<QuotaLedgerRow> = [
    { title: t('quotaLedgerDate'), dataIndex: 'createdAt', render: (v: number) => <Typography.Text type="secondary">{fmt(v)}</Typography.Text> },
    {
      title: t('quotaLedgerType'),
      dataIndex: 'type',
      render: (v: QuotaLedgerRow['type']) => <Tag color={typeTag[v].color}>{typeTag[v].label}</Tag>,
    },
    {
      title: t('quotaLedgerDelta'),
      dataIndex: 'delta',
      align: 'right' as const,
      render: (v: number) => (
        <Typography.Text style={{ color: v > 0 ? '#4ade80' : v < 0 ? '#f87171' : undefined }}>
          {v > 0 ? `+${v}` : v}
        </Typography.Text>
      ),
    },
    { title: t('quotaLedgerBalance'), dataIndex: 'balanceAfter', align: 'right' as const },
    { title: t('quotaLedgerReason'), dataIndex: 'reason', render: (v: string | null) => v ?? '—' },
    {
      title: t('quotaLedgerDisk'),
      key: 'disk',
      render: (_: unknown, r) =>
        r.diskSerial ? (
          <Typography.Text code>{r.diskSerial}</Typography.Text>
        ) : r.deviceLabel ? (
          <Typography.Text type="secondary">{r.deviceLabel}</Typography.Text>
        ) : (
          '—'
        ),
    },
  ];
  return (
    <Card title={t('quotaHistoryTitle')}>
      <Table<QuotaLedgerRow>
        size="small"
        rowKey="id"
        columns={columns}
        dataSource={q.data?.rows ?? []}
        loading={q.isLoading}
        pagination={{ pageSize: 12, hideOnSinglePage: true }}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: t('quotaHistoryEmpty') }}
      />
    </Card>
  );
}

function RecoveryCodesPanel({ justIssued, dismiss }: { justIssued: string[] | null; dismiss: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['recovery-codes-count'], queryFn: api.recoveryCodesCount, refetchInterval: 30_000 });
  const [shown, setShown] = useState<string[] | null>(null);
  const regen = useMutation({
    mutationFn: api.recoveryCodesRegenerate,
    onSuccess: (r) => {
      setShown(r.recoveryCodes);
      qc.invalidateQueries({ queryKey: ['recovery-codes-count'] });
    },
  });

  const display = shown ?? justIssued;
  const copy = async (): Promise<void> => {
    if (!display) return;
    try {
      await navigator.clipboard.writeText(display.join('\n'));
    } catch {
      /* clipboard blocked */
    }
  };
  const download = (): void => {
    if (!display) return;
    const blob = new Blob([display.join('\n') + '\n'], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ddisk-recovery-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Card title={t('recoveryCodesTitle')}>
      <Typography.Paragraph type="secondary">{t('recoveryCodesIntro')}</Typography.Paragraph>
      {q.data && (
        <Typography.Paragraph>
          {t('recoveryCodesRemaining')}: <strong>{q.data.remaining}</strong> / 10
        </Typography.Paragraph>
      )}
      {display && (
        <Card type="inner" style={{ marginBottom: 12 }}>
          <Typography.Paragraph>{t('recoveryCodesIssuedNote')}</Typography.Paragraph>
          <pre style={{ background: 'rgba(127,127,127,0.1)', padding: 8, borderRadius: 3, margin: 0, userSelect: 'all' }}>
            {display.join('\n')}
          </pre>
          <Space style={{ marginTop: 10 }}>
            <Button icon={<CopyOutlined />} onClick={copy}>{t('recoveryCodesCopy')}</Button>
            <Button icon={<DownloadOutlined />} onClick={download}>{t('recoveryCodesDownload')}</Button>
            <Button icon={<EyeInvisibleOutlined />} onClick={() => { setShown(null); dismiss(); }}>{t('recoveryCodesHide')}</Button>
          </Space>
        </Card>
      )}
      <Popconfirm title={t('recoveryCodesRegenConfirm')} onConfirm={() => regen.mutate()}>
        <Button icon={<ReloadOutlined />} loading={regen.isPending}>{t('recoveryCodesRegenBtn')}</Button>
      </Popconfirm>
    </Card>
  );
}

interface SessionGroup {
  key: string;
  ip: string | null;
  userAgent: string | null;
  ids: string[];
  count: number;
  lastActive: number;
  isCurrent: boolean;
}

function ActiveSessions() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['auth-sessions'], queryFn: api.listAuthSessions, refetchInterval: 10_000 });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['auth-sessions'] });
  const revokeGroup = useMutation({
    mutationFn: (ids: string[]) => api.revokeAuthSessionGroup(ids),
    onSuccess: invalidate,
  });
  const revokeOthers = useMutation({
    mutationFn: () => api.revokeOtherAuthSessions(),
    onSuccess: invalidate,
  });
  const fmt = fmtDateTimeMin;

  // Group the flat session list by device (IP + User-Agent).
  const groups: SessionGroup[] = [];
  const byKey = new Map<string, SessionGroup>();
  for (const s of q.data ?? []) {
    const key = `${s.ip ?? ''}|${s.userAgent ?? ''}`;
    let g = byKey.get(key);
    if (!g) {
      g = { key, ip: s.ip, userAgent: s.userAgent, ids: [], count: 0, lastActive: 0, isCurrent: false };
      byKey.set(key, g);
      groups.push(g);
    }
    g.ids.push(s.id);
    g.count++;
    g.lastActive = Math.max(g.lastActive, s.createdAt);
    g.isCurrent = g.isCurrent || s.isCurrent;
  }
  // Current device first, then most-recently-active.
  groups.sort((a, b) => Number(b.isCurrent) - Number(a.isCurrent) || b.lastActive - a.lastActive);
  const hasOthers = groups.some((g) => !g.isCurrent);

  const columns: ColumnsType<SessionGroup> = [
    {
      title: t('authSessionDevice'),
      key: 'device',
      render: (_: unknown, g) => (
        <Space>
          <Typography.Text>{deviceLabel(g.userAgent)}</Typography.Text>
          {g.isCurrent && <Tag color="green">{t('authSessionCurrent')}</Tag>}
        </Space>
      ),
    },
    {
      title: t('authSessionIp'),
      dataIndex: 'ip',
      render: (v: string | null) => <Typography.Text code>{v ?? '—'}</Typography.Text>,
    },
    { title: t('authSessionCount'), dataIndex: 'count', align: 'right' as const },
    {
      title: t('authSessionLastActive'),
      dataIndex: 'lastActive',
      render: (v: number) => <Typography.Text type="secondary">{fmt(v)}</Typography.Text>,
    },
    {
      title: t('colActions'),
      key: 'actions',
      align: 'right' as const,
      render: (_: unknown, g) => (
        <Popconfirm title={t('authSessionRevokeGroupConfirm')} onConfirm={() => revokeGroup.mutate(g.ids)}>
          <Button danger size="small" icon={<LogoutOutlined />} loading={revokeGroup.isPending}>
            {t('authSessionRevokeGroup')}
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <Card
      title={t('authSessionsTitle')}
      extra={
        hasOthers && (
          <Popconfirm title={t('authSessionRevokeOthersConfirm')} onConfirm={() => revokeOthers.mutate()}>
            <Button size="small" icon={<LogoutOutlined />} loading={revokeOthers.isPending}>
              {t('authSessionRevokeOthers')}
            </Button>
          </Popconfirm>
        )
      }
    >
      <Table<SessionGroup>
        size="small"
        rowKey="key"
        columns={columns}
        dataSource={groups}
        loading={q.isLoading}
        pagination={false}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: t('emptyList') }}
      />
    </Card>
  );
}
